import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { AgGridReact } from 'ag-grid-react';

import { useGetPractitionersEnrolled } from 'lib/hooks/queries/practitioners/useGetPractitionersEnrolled';

import CustomPagination from 'components/shared/CustomPagination/CustomPagination';
import Loader from 'components/shared/Loader/Loader';

import { getColumns } from '../lib/helpers';

import SelectionActionBar from './StaffPrescribeTable/components/SelectionActionBar';
import StaffModals from './StaffPrescribeTable/components/StaffModals';
import { useStaffEnrollment } from './StaffPrescribeTable/hooks/useStaffEnrollment';

const MIN_ITEMS_FOR_CONTROLS = 10;
const PAGE_SIZE = 10;

const StaffPrescribeTable = ({ isAdmin }) => {
  const { practiceId } = useOutletContext();
  const gridRef = useRef(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, refetch } = useGetPractitionersEnrolled({
    params: { practice_id: Number(practiceId), withCount: true },
    dependencies: [practiceId]
  });

  const clearSelection = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.deselectAll();
    }
    setSelectedRows([]);
  }, []);

  const {
    enrollModalOpen,
    unenrollConfirmOpen,
    selectedProvider,
    enrollMutation,
    unenrollMutation,
    handleEnrollRow,
    handleUnenrollRow,
    handleEnrollSelected,
    handleUnenrollSelected,
    closeEnrollModal,
    closeUnenrollConfirm,
    handleUnenrollConfirm
  } = useStaffEnrollment({ refetch, clearSelection });

  const users = data?.users || [];
  const totalItems = users.length;
  const showControls = totalItems > MIN_ITEMS_FOR_CONTROLS;

  const paginatedUsers = useMemo(() => {
    if (!showControls) return users;
    const start = (currentPage - 1) * PAGE_SIZE;
    return users.slice(start, start + PAGE_SIZE);
  }, [users, currentPage, showControls]);

  const onSelectionChanged = useCallback(() => {
    if (gridRef.current?.api) {
      const selectedNodes = gridRef.current.api.getSelectedNodes();
      setSelectedRows(selectedNodes.map((node) => node.data));
    }
  }, []);

  const defaultColDef = useMemo(
    () => ({ sortable: true, resizable: true, suppressMenu: true }),
    []
  );

  const columnDefs = useMemo(
    () => getColumns({ onEnroll: handleEnrollRow, onUnenroll: handleUnenrollRow, isAdmin }),
    [handleEnrollRow, handleUnenrollRow, isAdmin]
  );

  const isAnySelectedEnrolled = selectedRows.some((row) => row?.veradigm_user_id);
  const isAnySelectedNotEnrolled = selectedRows.some((row) => !row?.veradigm_user_id);

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-20">
        <Loader color="primary" />
      </div>
    );
  }

  return (
    <div className="relative w-full flex-shrink-0 flex-grow-0">
      <div className="w-full overflow-hidden rounded-lg border border-solid border-neutral-200">
        <div className="ag-theme-quartz w-full [&_.ag-center-cols-container]:!min-h-0 [&_.ag-center-cols-viewport]:!min-h-0">
          <AgGridReact
            ref={gridRef}
            rowData={paginatedUsers}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowSelection="single"
            onSelectionChanged={onSelectionChanged}
            suppressRowClickSelection={true}
            domLayout="autoHeight"
            animateRows={true}
            suppressPaginationPanel={true}
          />
        </div>

        {showControls && (
          <div className="flex items-center justify-end border-t border-neutral-200 bg-white px-4 py-2">
            <CustomPagination
              page={currentPage}
              perPage={PAGE_SIZE}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      <SelectionActionBar
        totalSelected={selectedRows.length}
        isAnySelectedEnrolled={isAnySelectedEnrolled}
        isAnySelectedNotEnrolled={isAnySelectedNotEnrolled}
        onClearSelection={clearSelection}
        onEnroll={() => handleEnrollSelected(selectedRows)}
        onUnenroll={() => handleUnenrollSelected(selectedRows)}
        isEnrollLoading={enrollMutation.isPending}
        hidden={enrollModalOpen || unenrollConfirmOpen}
      />

      <StaffModals
        enrollModalOpen={enrollModalOpen}
        unenrollConfirmOpen={unenrollConfirmOpen}
        selectedProvider={selectedProvider}
        selectedRows={selectedRows}
        practiceId={practiceId}
        isAdmin={isAdmin}
        onCloseEnrollModal={closeEnrollModal}
        onCloseUnenrollConfirm={closeUnenrollConfirm}
        onEnrollSuccess={() => {
          refetch();
          clearSelection();
        }}
        onUnenrollConfirm={() => handleUnenrollConfirm(selectedRows, practiceId)}
        isUnenrollLoading={unenrollMutation.isPending}
      />
    </div>
  );
};

export default StaffPrescribeTable;
